/**
 * Editor Undo/Redo History.
 *
 * A lightweight command stack for the facility-map editor. Because edits
 * persist to the server immediately, each reversible action pushes a pair of
 * closures — `undo` restores the prior value, `redo` re-applies the new one —
 * that re-issue the relevant REST calls. Covers property/geometry changes
 * (move, resize, reshape, rotate, nudge, setback, boundary edits), which are
 * cleanly invertible by id. Create/delete stay outside the stack (they are
 * explicit, confirmed actions with unstable ids).
 */

import { useCallback, useRef, useState } from "react";

export interface HistoryEntry {
  label?: string;
  undo: () => void | Promise<void>;
  redo: () => void | Promise<void>;
}

export function useEditorHistory(limit = 50) {
  const undoRef = useRef<HistoryEntry[]>([]);
  const redoRef = useRef<HistoryEntry[]>([]);
  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  /** Record a reversible action (clears the redo stack). */
  const push = useCallback(
    (entry: HistoryEntry) => {
      undoRef.current = [...undoRef.current.slice(-(limit - 1)), entry];
      redoRef.current = [];
      bump();
    },
    [limit, bump],
  );

  const undo = useCallback(() => {
    const e = undoRef.current[undoRef.current.length - 1];
    if (!e) return;
    undoRef.current = undoRef.current.slice(0, -1);
    redoRef.current = [...redoRef.current, e];
    bump();
    void Promise.resolve(e.undo());
  }, [bump]);

  const redo = useCallback(() => {
    const e = redoRef.current[redoRef.current.length - 1];
    if (!e) return;
    redoRef.current = redoRef.current.slice(0, -1);
    undoRef.current = [...undoRef.current, e];
    bump();
    void Promise.resolve(e.redo());
  }, [bump]);

  const clear = useCallback(() => {
    undoRef.current = [];
    redoRef.current = [];
    bump();
  }, [bump]);

  return {
    push,
    undo,
    redo,
    clear,
    canUndo: undoRef.current.length > 0,
    canRedo: redoRef.current.length > 0,
  };
}
